import { PDF_WIDTH } from 'constant';
import FileSaver from 'file-saver';
import { useActions } from 'hooks';
import { jsPDF } from 'jspdf';
import { useCallback, useRef } from 'react';
import { DownloadType } from 'types';
import domtoimage from 'dom-to-image';

export const useImageDownload = () => {
  const divRef = useRef<HTMLDivElement>(null);

  const generateImage = async () => {
    if (divRef !== null && divRef?.current) {
      return await domtoimage.toPng(divRef.current, { bgcolor: '#ffffff' }).then((dataUrl: string) => dataUrl);
    }
  };

  const { setShowDwnLoader } = useActions();

  const handleCallDownload = useCallback(
    async (
      type: DownloadType,
      configuration: {
        fileName: string;
        orientation?: 'portrait' | 'landscape';
        isSquareDownload?: boolean;
      },
    ) => {
      const { fileName, orientation } = configuration;
      try {
        await new Promise((resolve) => setTimeout(resolve, 0));

        const image = await generateImage();
        if (image) {
          if (type === 'pdf') {
            if (divRef?.current) {
              const img = new Image();
              img.src = image;

              img.onload = () => {
                // A4 dimensions in mm
                const A4_WIDTH = 210; // Fixed A4 width in mm
                const A4_HEIGHT = 297; // Fixed A4 height in mm (portrait)
                const imgWidth = img.width;
                const imgHeight = img.height;

                let pdfWidth = A4_WIDTH;
                let pdfHeight = A4_HEIGHT;

                if (orientation === 'landscape') {
                  // Landscape: Scale to fill A4 landscape size
                  pdfWidth = A4_HEIGHT;
                  pdfHeight = A4_WIDTH;
                } else if (imgHeight > (A4_HEIGHT / A4_WIDTH) * imgWidth) {
                  // Portrait: Adjust height if image is taller than A4
                  pdfHeight = (imgHeight / imgWidth) * A4_WIDTH;
                }

                const pdf = new jsPDF({
                  orientation: orientation || 'portrait',
                  unit: 'mm',
                  format: [pdfWidth, pdfHeight],
                });

                // Calculate image scaling and offsets
                const scaledWidth = orientation === 'landscape' ? pdfWidth : A4_WIDTH;
                const scaledHeight =
                  orientation === 'landscape'
                    ? pdfHeight
                    : imgHeight > (A4_HEIGHT / A4_WIDTH) * imgWidth
                      ? pdfHeight
                      : (imgHeight / imgWidth) * A4_WIDTH;

                // Add the image at the top-left corner
                pdf.addImage(image, 'PNG', 0, 0, scaledWidth, scaledHeight);

                // Save the PDF
                pdf.save(`${fileName}.pdf`);
              };
            }
          } else {
            const extension = type === 'jpg' ? 'jpg' : 'png';
            FileSaver.saveAs(image, `${fileName}.${extension}`);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setShowDwnLoader(false);
      }
    },
    [divRef, generateImage],
  );

  return { handleCallDownload, divRef };
};